// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import usePermission from "@/hooks/usePermission";
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
import PhoneInput from "@/UI/inputs/PhoneInput";
import SelectInput from "@/UI/inputs/SelectInput";
import TextInput from "@/UI/inputs/TextInput";
// --- Logic --------------------------------------------------------------------------------------
export type TyAddMemberDetails = { onDone: (result: { name: string; password: string | null }) => void };

type Props = { onClose: () => void; details?: unknown };

/**
 * A side form, like every create and update form in this product. Adds a person by phone: an unknown
 * number creates an account and returns its first password once, a known one simply joins them here
 * with the password they already use elsewhere.
 */
export default function AddMemberPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { isOwner } = usePermission();
    const { onDone } = details as TyAddMemberDetails;

    const [name, setName] = useState("");
    const [phone, setPhone] = useState<string | undefined>("");
    const [role, setRole] = useState("member");
    const [formError, setFormError] = useState<string | null>(null);

    const { data, loading, error, succeededAt, invoke } = useFetch<{ member: { name: string }; password: string | null }>(
        "/team",
        { method: "POST" },
        false
    );

    useEffect(() => {
        if (!succeededAt || !data) return;
        onClose();
        onDone({ name: data.member.name, password: data.password });
        // One finished request, one handover. `onClose` and `onDone` are stable, so this runs once.
    }, [succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = () => {
        setFormError(null);
        const cleanPhone = (phone ?? "").replace(/\s/g, "");
        if (!name.trim()) return setFormError(t.team.nameRequired);
        if (cleanPhone.length < 8) return setFormError(t.team.phoneRequired);
        invoke({ method: "POST", data: { name: name.trim(), phone: cleanPhone, role } });
    };

    // A manager may only add members; offering a manager is the owner's option.
    const roleOptions = isOwner
        ? [
              { value: "member", label: t.roles.member },
              { value: "manager", label: t.roles.manager },
          ]
        : [{ value: "member", label: t.roles.member }];

    return (
        <section className="side-form">
            <header className="side-form__header">
                <h2 className="side-form__title">{t.team.addTitle}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                <p className="side-form__hint">{t.team.addHint}</p>

                <TextInput value={name} onChange={setName} label={t.team.name} placeholder={t.team.namePlaceholder} disabled={loading} />
                <PhoneInput value={phone} onChange={setPhone} placeholder={t.auth.phonePlaceholder} disabled={loading} />
                <SelectInput value={role} onChange={setRole} options={roleOptions} label={t.team.role} disabled={loading} />

                {(formError || error) && <p className="side-form__error">{formError ?? error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={loading}>
                    {loading ? t.common.loading : t.team.add}
                </button>
            </footer>
        </section>
    );
}
